import React, { useContext, useEffect, useState } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Typography, Grid, Box } from "@mui/material";
import { ThemeContext } from "../../../context/ThemeContext";
import { useTranslation } from "react-i18next";
import BidValue from '../../global/BidAsk/BidValue';
import AskValue from '../../global/BidAsk/AskValue';
import toast from "react-hot-toast";
import { addJournalLog } from "../../tab/tabview/journal/addJournalLog";
import { getCookies } from "../../../services/Cookies";
import { shallowEqual, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const ClosePosition = ({ open, onClose, selectedPosition, onConfirm }) => {
    const { darkMode } = useContext(ThemeContext)
    const { t } = useTranslation()
    const [amount, setAmount] = useState(selectedPosition?.amount)
    const accountId = localStorage.getItem('accountID')
    const username = getCookies('username')
    const allSymbolParams = useSelector((state) => state.symbol.allSymbolParams, shallowEqual);
    const decimalPlaces = useSelector((state) => state.user.decimalPlacesForAmountFormatting);
    const symbolParameterData = selectedPosition.symbolId ? allSymbolParams[selectedPosition.symbolId] : {};

    useEffect(() => {
        if (selectedPosition) {
            setAmount(selectedPosition.amount ?? "");
        }
    }, [selectedPosition])

    const handleConfirm = () => {
        const inputAmount = Number(amount);
        const maxAmount = Number(selectedPosition?.amount);

        if (isNaN(inputAmount) || inputAmount <= 0) {
            toast.error("Amount must be a valid number greater than 0.");
            return;
        }

        if (inputAmount > maxAmount) {
            toast.error(`Amount must not exceed ${maxAmount}`);
            return;
        }

        onConfirm(inputAmount);

        if (inputAmount === maxAmount) {
            addJournalLog('tradeClosed', `Trade closed: ID #${selectedPosition.id}, ${selectedPosition.symbolName}, at ${selectedPosition.openPrice}, ${selectedPosition.side === 1 ? "Buy" : "Sell"}, Amount: ${selectedPosition.amount}, P/L: ${selectedPosition.profitLoss}, Account ${username}: ${accountId}.`);
        } else {
            addJournalLog('closedPartiallyPosition', `Position #${selectedPosition.id} has been closed partially, Account ${username}: ${accountId}.`);
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle sx={{ color: "white", p: 1 }} className="bg-primary">
                <Typography variant="body1" sx={{ color: darkMode ? "var(--white)" : "var(--white)" }} align="center" >{t('ClosePosition')} ({selectedPosition?.symbolName})</Typography>
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: darkMode ? "#151924" : "#f5f5f5", }}>
                <Grid container spacing={2} sx={{ mt: 1 }} alignItems='center'>
                    <Grid item xs={6} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>{t('bid')}</Typography>
                        <Box sx={{ display: "flex", justifyContent: 'center' }}><BidValue symbolId={selectedPosition?.symbolId} digits={selectedPosition?.digits} /></Box>
                    </Grid>
                    <Grid item xs={6} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ textAlign: 'center' }}>{t('ask')}</Typography>
                        <Box sx={{ display: "flex", justifyContent: 'center' }}><AskValue symbolId={selectedPosition?.symbolId} digits={selectedPosition?.digits} /></Box>
                    </Grid>
                    <Grid item xs={3}><Typography variant="subtitle2" sx={{ textAlign: 'right' }}>{t('amount')}</Typography></Grid>
                    {/* <Grid item xs={9}><TextField value={amount} inputProps={{ max: selectedPosition?.amount ?? undefined, }} onChange={(e) => setAmount(e.target.value)} fullWidth /></Grid> */}
                    <Grid item xs={9}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0, border: '1px solid', borderColor: 'divider', backgroundColor: darkMode ? "#1e2533" : "#fff" }}>
                            <Button variant="" size="small" disabled={Number(amount) <= symbolParameterData.minimumAmountPerDeal} sx={{ borderRadius: 0, "&.Mui-disabled": { opacity: 1, backgroundColor: darkMode ? "#3a3f45" : "#e0e0e0" } }}
                                onClick={() => {
                                    setAmount((prev) => {
                                        const newAmount = Math.max(symbolParameterData.minimumAmountPerDeal, Number(prev) - symbolParameterData.minimumAmountPerDeal);
                                        return newAmount.toFixed(decimalPlaces);
                                    });
                                }}><RemoveIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }} /></Button>

                            <TextField fullWidth type="number" value={amount || ''} onChange={(e) => setAmount(e.target.value)}
                                inputProps={{
                                    min: symbolParameterData.minimumAmountPerDeal,
                                    max: selectedPosition?.amount ?? undefined,
                                    step: symbolParameterData.minimumAmountPerDeal,
                                    style: { textAlign: 'center', }
                                }}
                                sx={{ "& .MuiInputBase-input": { textAlign: "center", }, "& .MuiOutlinedInput-notchedOutline": { border: "none" }, color: darkMode ? 'var(--lightText)' : "var(--black)", p: 0, }}
                            />

                            <Button variant="" size="small" disabled={Number(amount) >= Number(selectedPosition?.amount)} sx={{ borderRadius: 0, "&.Mui-disabled": { opacity: 1, backgroundColor: darkMode ? "#3a3f45" : "#e0e0e0" } }}
                                onClick={() => {
                                    setAmount((prev) => {
                                        const newAmount = Math.min(Number(selectedPosition?.amount), Number(prev) + symbolParameterData.minimumAmountPerDeal);
                                        return newAmount.toFixed(decimalPlaces);
                                    });
                                }}>
                                <AddIcon fontSize="small" sx={{ fontSize: '1rem', color: darkMode ? 'var(--lightText)' : "var(--black)", }} />
                            </Button>
                        </Box>
                    </Grid>

                </Grid>
            </DialogContent>
            <DialogActions sx={{ backgroundColor: darkMode ? "#151924" : "#f5f5f5", }}>
                <Button onClick={onClose} variant="outlined">{t('cancel')}</Button>
                <Button onClick={handleConfirm} color="primary" variant="contained">{t('ClosePosition')}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClosePosition;
